/**
 * Google Analytics 4 tracking utilities
 *
 * Provides a simple wrapper around GA4 gtag.js for tracking page views and events.
 * Only initializes GA if a valid GA ID is provided (per-tenant configuration).
 *
 * @module analytics
 */

let initialized = false;
let currentId = null;

/**
 * Initialize Google Analytics 4 with the provided measurement ID
 *
 * @param {string} gaId - Google Analytics 4 measurement ID (e.g., 'G-XXXXXXXXXX')
 * @returns {void}
 */
export const initGA = (gaId) => {
  // Skip if no GA ID provided
  if (!gaId || typeof gaId !== 'string') {
    return;
  }

  // Skip if already initialized with the same ID
  if (initialized && currentId === gaId) {
    return;
  }

  try {
    // Inject GA4 script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(script);

    // Initialize dataLayer and gtag function
    window.dataLayer = window.dataLayer || [];
    const gtag = function () {
      window.dataLayer.push(arguments);
    };
    window.gtag = window.gtag || gtag;

    // Configure GA4 with manual page view tracking
    window.gtag('js', new Date());
    window.gtag('config', gaId, {
      send_page_view: false // We'll manually track page views
    });

    initialized = true;
    currentId = gaId;
  } catch (error) {
    console.error('Failed to initialize Google Analytics:', error);
  }
};

/**
 * Track a custom event in Google Analytics
 *
 * @param {string} eventName - Name of the event (e.g., 'purchase', 'begin_checkout')
 * @param {Object} params - Event parameters (e.g., { transaction_id: '123', value: 100 })
 * @returns {void}
 */
export const trackEvent = (eventName, params = {}) => {
  // Log event for debugging (helpful for local testing)
  console.log('[GA4 Event]', eventName, params);

  // Skip if GA is not available
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }

  try {
    window.gtag('event', eventName, params);
  } catch (error) {
    console.error('Failed to track event:', eventName, error);
  }
};

/**
 * Track a page view in Google Analytics
 *
 * @param {string} path - Page path (e.g., '/itineraries/123')
 * @param {Object} extraParams - Additional parameters to include with the page view
 * @param {string} extraParams.page_title - Optional page title (defaults to document.title)
 * @returns {void}
 */
export const trackPageView = (path, extraParams = {}) => {
  // Extract page_title from extraParams to avoid duplication
  const { page_title, ...otherParams } = extraParams;

  // Use provided page_title or fall back to current document.title
  const pageTitle = page_title || document.title;

  const params = {
    page_path: path,
    page_title: pageTitle,
    ...otherParams
  };

  console.log('[GA4 Page View]', path, params);
  trackEvent('page_view', params);
};
